export default function DrivePropertiesCard({fileData, lang}) {
    if (!fileData) return null

    const formatDate = (date) =>
        date ? new Date(date).toLocaleString() : '—'

    const formatSize = (size) => {
        if (size === undefined || size === null) return 'N/A'
        const num = Number(size)
        if (isNaN(num)) return 'N/A'

        if (num >= 1024 * 1024 * 1024) {
            return `${(num / (1024 * 1024 * 1024)).toFixed(2)} GB`
        }
        if (num >= 1024 * 1024) {
            return `${(num / (1024 * 1024)).toFixed(2)} MB`
        }
        if (num >= 1024) {
            return `${(num / 1024).toFixed(2)} KB`
        }
        return `${num} B`
    }

    const getFileTypeInfo = (file) => {
        if (!file) return {label: 'File', icon: '📄', isFolder: false}

        const {mimeType, name}

            = file

        const googleMap = {
            'application/vnd.google-apps.document': {label: 'Google Docs', icon: '📘'},
            'application/vnd.google-apps.spreadsheet': {label: 'Google Sheets', icon: '📗'},
            'application/vnd.google-apps.presentation': {label: 'Google Slides', icon: '📙'},
            'application/vnd.google-apps.form': {label: 'Google Form', icon: '📝'},
            'application/vnd.google-apps.drawing': {label: 'Google Drawing', icon: '🎨'},
            'application/vnd.google-apps.folder': {label: 'Folder', icon: '📁'}
        }

        if (googleMap[mimeType]) {
            return {
                ...googleMap[mimeType],
                isFolder: mimeType === 'application/vnd.google-apps.folder'
            }
        }

        const parts = name?.split('.') || []
        const ext = parts.length > 1 ? parts.pop().toLowerCase() : ''

        const extMap = {
            pdf: {label: 'PDF file', icon: '📕'},
            doc: {label: 'Word file', icon: '📘'},
            docx: {label: 'Word file', icon: '📘'},
            xls: {label: 'Excel file', icon: '📗'},
            xlsx: {label: 'Excel file', icon: '📗'},
            csv: {label: 'CSV file', icon: '📗'},
            ppt: {label: 'PowerPoint file', icon: '📙'},
            pptx: {label: 'PowerPoint file', icon: '📙'},
            jpg: {label: 'Image file', icon: '🖼️'},
            jpeg: {label: 'Image file', icon: '🖼️'},
            png: {label: 'Image file', icon: '🖼️'},
            gif: {label: 'Image file', icon: '🖼️'},
            mp4: {label: 'Video file', icon: '🎬'},
            mov: {label: 'Video file', icon: '🎬'},
            mp3: {label: 'Audio file', icon: '🎵'},
            wav: {label: 'Audio file', icon: '🎵'},
            zip: {label: 'Archive file', icon: '🗜️'},
            rar: {label: 'Archive file', icon: '🗜️'},
            txt: {label: 'Text file', icon: '📄'},
            js: {label: 'JavaScript file', icon: '💻'},
            ts: {label: 'TypeScript file', icon: '💻'},
            json: {label: 'JSON file', icon: '💻'},
            html: {label: 'HTML file', icon: '🌐'},
            css: {label: 'CSS file', icon: '🎨'}
        }

        if (extMap[ext]) {
            return {...extMap[ext], isFolder: false}
        }

        return {
            label: ext ? `${ext.toUpperCase()} file` : 'File',
            icon: '📄',
            isFolder: false
        }
    }

    const typeInfo = getFileTypeInfo(fileData)
    const isFolder = typeInfo.isFolder

    const borderThumb = fileData.fileType === 'google-file' ? 'solid 2px gray' : ''

    if (typeInfo.label === 'Folder') {
        typeInfo.label = lang.body.googlePropertiesPage.folder
    }

    return (
        <div style={{padding: 20}}>

            {fileData.thumb && (
                <img
                    src={fileData.thumb}
                    alt="thumbnail"
                    className={'drive-prop-card-thumb'}
                    style={{border: borderThumb}}
                />
            )}

            <div className={'drive-prop-card'}>

                <div className={'drive-prop-card-header'}
                     style={{color: fileData.color}}>
                    {typeInfo.icon} {typeInfo.label}
                </div>

                <div className={'drive-prop-card-row'}>
                    <span className={'drive-prop-card-label'}>
                        {lang.body.googlePropertiesPage.created}
                    </span>
                    <span>
                        {formatDate(fileData['createdTime'])}
                    </span>
                </div>

                <div className={'drive-prop-card-row'}>
                    <span className={'drive-prop-card-label'}>
                        {lang.body.googlePropertiesPage.modified}
                    </span>
                    <span>
                        {formatDate(fileData['modifiedTime'])}
                    </span>
                </div>

                {isFolder && fileData['folderInfo'] && (
                    <>
                        <div className={'drive-prop-card-row'}>
                            <span className={'drive-prop-card-label'}>
                                {lang.body.googlePropertiesPage.items}
                            </span>
                            <span>
                                {fileData['folderInfo']['itemCount']}
                            </span>
                        </div>

                        <div className={'drive-prop-card-row'}>
                            <span className={'drive-prop-card-label'}>
                                {lang.body.googlePropertiesPage.totalSize}
                            </span>
                            <span>
                                {formatSize(
                                    fileData['folderInfo']['totalSizeBytes'] ||
                                    fileData['folderInfo']['totalSizeMB'] * 1024 * 1024
                                )}
                            </span>
                        </div>
                    </>
                )}

                {!isFolder && (
                    <div className={'drive-prop-card-row'}>
                        <span className={'drive-prop-card-label'}>
                            {lang.body.googlePropertiesPage.totalSize}
                        </span>
                        <span>
                            {formatSize(
                                fileData['sizeBytes'] ||
                                fileData['sizeMB'] * 1024 * 1024
                            )}
                        </span>
                    </div>
                )}

                <div className={'drive-prop-card-row'}>
                    <span className={'drive-prop-card-label'}>
                        {lang.body.googlePropertiesPage.owners}
                    </span>
                    <span>
                        {fileData['owners']?.map(o => o['emailAddress']).join(', ') || '—'}
                    </span>
                </div>

                <a href={fileData['webViewLink']}
                   className={'drive-prop-card-link'}
                   target={"_blank"}
                   rel={"noreferrer"}
                >{lang.body.googlePropertiesPage.openInDrive}</a>

            </div>
        </div>
    )
}